import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/unassigned-management.css";
import TopTabs from "../components/layout/TopTabs";
import ConfirmModal from "../components/ui/ConfirmModal";
import {
  getProcessesWithoutTasks,
  getTasksWithoutProcesses,
  getTasksWithoutJobs,
  getJobsWithoutTasks,
  getJobsWithoutTable,
} from "../services/unassignedEntitiesService";
import {
  deleteProcess,
  getProcessesByCompanyId,
} from "../services/processService";
import { deleteTask, getTasksByCompanyId } from "../services/taskService";
import { deleteJob } from "../services/jobService";
import { getBuildingsByCompanyId } from "../services/buildingService";
import { getFloorsByBuildingId } from "../services/floorService";
import { useAppContext } from "../context/AppContext";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";

const DEFAULT_PAGE_SIZE = 10;
const ROW_LIMIT_OPTIONS = [10, 25, 50, 100];
const VALID_TABS = [
  "processes",
  "tasks",
  "tasksNoJobs",
  "jobsNoTasks",
  "jobsNoTable",
];

const useDebouncedValue = (value, delay = 450) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const UnassignedEntitiesPage = () => {
  const { setActiveSection, setFloorId, setFloorFormMode } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();
  // Check if we need to restore a specific tab
  const storedTab = localStorage.getItem("unassignedEntitiesActiveTab");
  const initialTab = searchParams.get("tab") || storedTab || "processes";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [currentPage, setCurrentPage] = useState(() => {
    const raw = Number(searchParams.get("page") || 1);
    return Number.isInteger(raw) && raw > 0 ? raw : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const raw = Number(searchParams.get("limit") || DEFAULT_PAGE_SIZE);
    return ROW_LIMIT_OPTIONS.includes(raw) ? raw : DEFAULT_PAGE_SIZE;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // State for each tab
  const [processesWithoutTasks, setProcessesWithoutTasks] = useState([]);
  const [tasksWithoutProcesses, setTasksWithoutProcesses] = useState([]);
  const [tasksWithoutJobs, setTasksWithoutJobs] = useState([]);
  const [jobsWithoutTasks, setJobsWithoutTasks] = useState([]);
  const [jobsWithoutTable, setJobsWithoutTable] = useState([]);

  // Search queries for each tab
  const [processesSearchQuery, setProcessesSearchQuery] = useState("");
  const [tasksSearchQuery, setTasksSearchQuery] = useState("");
  const [tasksNoJobsSearchQuery, setTasksNoJobsSearchQuery] = useState("");
  const [jobsNoTasksSearchQuery, setJobsNoTasksSearchQuery] = useState("");
  const [jobsNoTableSearchQuery, setJobsNoTableSearchQuery] = useState("");

  const processesSearchDebounced = useDebouncedValue(processesSearchQuery);
  const tasksSearchDebounced = useDebouncedValue(tasksSearchQuery);
  const tasksNoJobsSearchDebounced = useDebouncedValue(tasksNoJobsSearchQuery);
  const jobsNoTasksSearchDebounced = useDebouncedValue(jobsNoTasksSearchQuery);
  const jobsNoTableSearchDebounced = useDebouncedValue(jobsNoTableSearchQuery);

  // Delete modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // 'process', 'task', or 'job'

  // Assign task to process modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [availableProcesses, setAvailableProcesses] = useState([]);
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  const [selectedProcessId, setSelectedProcessId] = useState("");
  const [processSearchQuery, setProcessSearchQuery] = useState("");

  // Assign task to job modal state
  const [assignTaskToJobModalOpen, setAssignTaskToJobModalOpen] =
    useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [taskSearchQuery, setTaskSearchQuery] = useState("");

  // Assign floor to job (Jobs without Table) modal state
  const [assignFloorToJobModalOpen, setAssignFloorToJobModalOpen] =
    useState(false);
  const [selectedJobForFloor, setSelectedJobForFloor] = useState(null);
  const [modalStep, setModalStep] = useState(1); // 1 = select building, 2 = select floor
  const [availableBuildings, setAvailableBuildings] = useState([]);
  const [availableFloors, setAvailableFloors] = useState([]);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [loadingFloors, setLoadingFloors] = useState(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState("");
  const [selectedFloorId, setSelectedFloorId] = useState("");
  const [buildingSearchQuery, setBuildingSearchQuery] = useState("");
  const [floorSearchQuery, setFloorSearchQuery] = useState("");

  const [paginationByTab, setPaginationByTab] = useState({
    processes: { total: 0, totalPages: 1, count: 0 },
    tasks: { total: 0, totalPages: 1, count: 0 },
    tasksNoJobs: { total: 0, totalPages: 1, count: 0 },
    jobsNoTasks: { total: 0, totalPages: 1, count: 0 },
    jobsNoTable: { total: 0, totalPages: 1, count: 0 },
  });

  // Dropdown state for action menus
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  const normalizePaginatedResponse = useCallback(
    (response) => {
      const toNumber = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      if (Array.isArray(response)) {
        const total = response.length;
        return {
          rows: response,
          total,
          totalPages: Math.max(1, Math.ceil(total / pageSize)),
        };
      }

      const nestedData = response?.data;
      const rows =
        Array.isArray(nestedData?.data) ? nestedData.data
        : Array.isArray(nestedData) ? nestedData
        : Array.isArray(response?.rows) ? response.rows
        : [];

      const pagination =
        response?.pagination ||
        response?.meta ||
        nestedData?.pagination ||
        nestedData?.meta ||
        {};
      const totalFromPagination =
        pagination.total ||
        pagination.total_count ||
        pagination.totalItems ||
        pagination.total_items ||
        response?.total ||
        nestedData?.total;

      const total = toNumber(totalFromPagination) ?? rows.length;

      const totalPagesFromPagination =
        pagination.totalPages ||
        pagination.total_pages ||
        pagination.pages ||
        response?.totalPages ||
        response?.total_pages ||
        nestedData?.totalPages ||
        nestedData?.total_pages;

      const totalPages =
        toNumber(totalPagesFromPagination) ??
        Math.max(1, Math.ceil(total / pageSize));

      return { rows, total, totalPages: Math.max(1, totalPages) };
    },
    [pageSize],
  );

  const renderPagination = (tabId) => {
    const meta = paginationByTab[tabId] || { totalPages: 1 };
    const totalPages = Math.max(1, Number(meta.totalPages) || 1);
    const totalRows = Number(meta.total) || 0;
    const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const showingTo =
      totalRows === 0 ? 0 : Math.min(currentPage * pageSize, totalRows);

    const visiblePageItems = (() => {
      if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
      }

      if (currentPage <= 2) {
        return [1, 2, 3, "ellipsis", totalPages];
      }

      if (currentPage === 3) {
        return [1, 2, 3, 4, "ellipsis", totalPages];
      }

      if (currentPage === totalPages - 2) {
        return [
          1,
          "ellipsis",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages,
        ];
      }

      if (currentPage >= totalPages - 1) {
        return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
      }

      return [
        1,
        "ellipsis",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "ellipsis",
        totalPages,
      ];
    })();

    if (totalPages <= 1) return null;
    return (
      <div className="unassigned-pagination-bottom">
        <div className="unassigned-pagination-summary">
          Showing {showingFrom} to {showingTo} of {totalRows} entries
        </div>
        <div className="unassigned-pagination-actions">
          <button
            className="secondary-btn sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
          >
            Previous
          </button>

          {visiblePageItems.map((item, index) => {
            if (item === "ellipsis") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  style={{ padding: "0 6px", color: "#6b7280" }}
                  aria-hidden="true"
                >
                  …
                </span>
              );
            }

            const page = Number(item);
            return (
              <button
                key={page}
                className={
                  page === currentPage ? "primary-btn sm" : "secondary-btn sm"
                }
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            );
          })}

          <button
            className="secondary-btn sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const renderSearchBox = ({
    tabId,
    value,
    onChange,
    onClear,
    placeholder,
    found,
    total,
  }) => (
    <div className="unassigned-search-row">
      <div className="unassigned-limit-group">
        <label className="unassigned-limit-label">Rows per page:</label>
        <select
          value={pageSize}
          onChange={(e) => {
            const nextLimit = Number(e.target.value);
            setPageSize(nextLimit);
            setCurrentPage(1);
          }}
        >
          {ROW_LIMIT_OPTIONS.map((option) => (
            <option key={`${tabId}-${option}`} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      <div>
        <div className="roles-search">
          <input
            type="text"
            className="search-input"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
          />
          <button
            className="secondary-btn sm"
            onClick={onClear}
            disabled={!value}
          >
            Clear
          </button>
        </div>
        {value && (
          <div className="unassigned-search-found">
            Found {found} of {total}
          </div>
        )}
      </div>
    </div>
  );

  // Cleanup stored tab on mount
  useEffect(() => {
    return () => {
      localStorage.removeItem("unassignedEntitiesActiveTab");
    };
  }, []);

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    const pageFromUrl = Number(searchParams.get("page") || 1);
    const limitFromUrl = Number(searchParams.get("limit") || DEFAULT_PAGE_SIZE);
    if (tabFromUrl && VALID_TABS.includes(tabFromUrl)) {
      setActiveTab((prev) => (prev === tabFromUrl ? prev : tabFromUrl));
    }
    if (Number.isInteger(pageFromUrl) && pageFromUrl > 0) {
      setCurrentPage((prev) => (prev === pageFromUrl ? prev : pageFromUrl));
    }
    if (ROW_LIMIT_OPTIONS.includes(limitFromUrl)) {
      setPageSize((prev) => (prev === limitFromUrl ? prev : limitFromUrl));
    }
  }, [searchParams]);

  useEffect(() => {
    const tabStr = String(activeTab);
    const pageStr = String(currentPage);
    const limitStr = String(pageSize);

    const sameTab = searchParams.get("tab") === tabStr;
    const samePage = searchParams.get("page") === pageStr;
    const sameLimit = searchParams.get("limit") === limitStr;
    if (sameTab && samePage && sameLimit) return;

    const next = new URLSearchParams(searchParams);
    next.set("tab", tabStr);
    next.set("page", pageStr);
    next.set("limit", limitStr);
    setSearchParams(next, { replace: true });
  }, [activeTab, currentPage, pageSize, searchParams, setSearchParams]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const activeSearchDebounced = useMemo(() => {
    if (activeTab === "processes") return processesSearchDebounced;
    if (activeTab === "tasks") return tasksSearchDebounced;
    if (activeTab === "tasksNoJobs") return tasksNoJobsSearchDebounced;
    if (activeTab === "jobsNoTasks") return jobsNoTasksSearchDebounced;
    if (activeTab === "jobsNoTable") return jobsNoTableSearchDebounced;
    return "";
  }, [
    activeTab,
    processesSearchDebounced,
    tasksSearchDebounced,
    tasksNoJobsSearchDebounced,
    jobsNoTasksSearchDebounced,
    jobsNoTableSearchDebounced,
  ]);

  // Load data for the active tab using server-side pagination/search
  useEffect(() => {
    const loadTabData = async () => {
      try {
        setLoading(true);
        setError("");
        const params = {
          page: currentPage,
          limit: pageSize,
          search: activeSearchDebounced,
        };

        switch (activeTab) {
          case "processes":
            try {
              const processes = await getProcessesWithoutTasks(params);
              const normalized = normalizePaginatedResponse(processes);
              setProcessesWithoutTasks(normalized.rows);
              setPaginationByTab((prev) => ({
                ...prev,
                processes: {
                  total: normalized.total,
                  totalPages: normalized.totalPages,
                  count: normalized.rows.length,
                },
              }));
            } catch (e) {
              setProcessesWithoutTasks([]);
              setPaginationByTab((prev) => ({
                ...prev,
                processes: { total: 0, totalPages: 1, count: 0 },
              }));
              setError(e?.message || "Failed to load processes");
            }
            break;

          case "tasks":
            try {
              const tasks = await getTasksWithoutProcesses(params);
              const normalized = normalizePaginatedResponse(tasks);
              setTasksWithoutProcesses(normalized.rows);
              setPaginationByTab((prev) => ({
                ...prev,
                tasks: {
                  total: normalized.total,
                  totalPages: normalized.totalPages,
                  count: normalized.rows.length,
                },
              }));
            } catch (e) {
              setTasksWithoutProcesses([]);
              setPaginationByTab((prev) => ({
                ...prev,
                tasks: { total: 0, totalPages: 1, count: 0 },
              }));
              setError(e?.message || "Failed to load tasks");
            }
            break;

          case "tasksNoJobs":
            try {
              const tasks = await getTasksWithoutJobs(params);
              const normalized = normalizePaginatedResponse(tasks);
              setTasksWithoutJobs(normalized.rows);
              setPaginationByTab((prev) => ({
                ...prev,
                tasksNoJobs: {
                  total: normalized.total,
                  totalPages: normalized.totalPages,
                  count: normalized.rows.length,
                },
              }));
            } catch (e) {
              setTasksWithoutJobs([]);
              setPaginationByTab((prev) => ({
                ...prev,
                tasksNoJobs: { total: 0, totalPages: 1, count: 0 },
              }));
              setError(e?.message || "Failed to load tasks");
            }
            break;

          case "jobsNoTasks":
            try {
              const jobs = await getJobsWithoutTasks(params);
              const normalized = normalizePaginatedResponse(jobs);
              setJobsWithoutTasks(normalized.rows);
              setPaginationByTab((prev) => ({
                ...prev,
                jobsNoTasks: {
                  total: normalized.total,
                  totalPages: normalized.totalPages,
                  count: normalized.rows.length,
                },
              }));
            } catch (e) {
              setJobsWithoutTasks([]);
              setPaginationByTab((prev) => ({
                ...prev,
                jobsNoTasks: { total: 0, totalPages: 1, count: 0 },
              }));
              setError(e?.message || "Failed to load jobs");
            }
            break;

          case "jobsNoTable":
            try {
              const jobs = await getJobsWithoutTable(params);
              const normalized = normalizePaginatedResponse(jobs);
              setJobsWithoutTable(normalized.rows);
              setPaginationByTab((prev) => ({
                ...prev,
                jobsNoTable: {
                  total: normalized.total,
                  totalPages: normalized.totalPages,
                  count: normalized.rows.length,
                },
              }));
            } catch (e) {
              setJobsWithoutTable([]);
              setPaginationByTab((prev) => ({
                ...prev,
                jobsNoTable: { total: 0, totalPages: 1, count: 0 },
              }));
              setError(e?.message || "Failed to load jobs");
            }
            break;

          default:
            break;
        }
      } catch (e) {
        setError(e?.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    loadTabData();
  }, [
    activeTab,
    currentPage,
    pageSize,
    activeSearchDebounced,
    normalizePaginatedResponse,
  ]);

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);
    setCurrentPage(1);
    try {
      localStorage.setItem("unassignedEntitiesActiveTab", tabId);
    } catch {}
  };

  // Restore last active tab
  // eslint-disable-next-line no-unused-vars
  const defaultTab = useMemo(() => {
    try {
      return localStorage.getItem("unassignedEntitiesActiveTab") || "processes";
    } catch {
      return "processes";
    }
  }, []);

  // Helper to extract company name from various field structures
  const getCompanyName = (item) => {
    return (
      item?.company?.name || item?.company_name || item?.companyName || "-"
    );
  };

  // Helper to extract function name from various field structures
  const getFunctionName = (item) => {
    // Check for capital F (Function) and lowercase f (function)
    return (
      item?.Function?.name ||
      item?.function?.name ||
      item?.function_name ||
      item?.functionName ||
      "-"
    );
  };

  // Delete handlers
  const openDeleteModal = (item, type) => {
    setSelectedItem(item);
    setDeleteType(type);
    setConfirmOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setConfirmOpen(false);
    setSelectedItem(null);
    setDeleteType("");
  };

  const confirmDelete = async () => {
    if (!selectedItem) return;

    try {
      setDeleting(true);
      setError("");

      let id;
      switch (deleteType) {
        case "process":
          id = selectedItem.process_id || selectedItem.processId;
          if (!id) throw new Error("Process ID not found");
          await deleteProcess(id);
          toast.success("Process deleted successfully");
          setProcessesWithoutTasks((prev) =>
            prev.filter((p) => (p.process_id || p.processId) !== id),
          );
          break;

        case "task":
          id = selectedItem.task_id || selectedItem.taskId;
          if (!id) throw new Error("Task ID not found");
          await deleteTask(id);
          toast.success("Task deleted successfully");
          setTasksWithoutProcesses((prev) =>
            prev.filter((t) => (t.task_id || t.taskId) !== id),
          );
          break;

        case "job":
          id = selectedItem.job_id || selectedItem.jobId;
          if (!id) throw new Error("Job ID not found");
          await deleteJob(id);
          toast.success("Job deleted successfully");
          // Remove from both job lists
          setJobsWithoutTasks((prev) =>
            prev.filter((j) => (j.job_id || j.jobId) !== id),
          );
          setJobsWithoutTable((prev) =>
            prev.filter((j) => (j.job_id || j.jobId) !== id),
          );
          break;

        default:
          throw new Error("Unknown delete type");
      }

      setConfirmOpen(false);
      setSelectedItem(null);
      setDeleteType("");
    } catch (e) {
      setError(e?.message || "Failed to delete item");
      toast.error(e?.message || "Failed to delete item");
    } finally {
      setDeleting(false);
    }
  };

  const getDeleteMessage = () => {
    if (!selectedItem) return null;

    switch (deleteType) {
      case "process":
        return (
          <div>
            <p>Are you sure you want to delete this process?</p>
            <ul style={{ marginTop: 8 }}>
              <li>
                <strong>Name:</strong>{" "}
                {selectedItem.process_name || selectedItem.processName}
              </li>
              <li>
                <strong>Code:</strong>{" "}
                {selectedItem.process_code || selectedItem.processCode}
              </li>
              <li>
                <strong>Company:</strong> {getCompanyName(selectedItem)}
              </li>
            </ul>
          </div>
        );
      case "task":
        return (
          <div>
            <p>Are you sure you want to delete this task?</p>
            <ul style={{ marginTop: 8 }}>
              <li>
                <strong>Name:</strong>{" "}
                {selectedItem.task_name || selectedItem.taskName}
              </li>
              <li>
                <strong>Code:</strong>{" "}
                {selectedItem.task_code || selectedItem.taskCode}
              </li>
              <li>
                <strong>Company:</strong> {getCompanyName(selectedItem)}
              </li>
            </ul>
          </div>
        );
      case "job":
        return (
          <div>
            <p>Are you sure you want to delete this job?</p>
            <ul style={{ marginTop: 8 }}>
              <li>
                <strong>Name:</strong>{" "}
                {selectedItem.job_name ||
                  selectedItem.jobName ||
                  selectedItem.name}
              </li>
              <li>
                <strong>Code:</strong>{" "}
                {selectedItem.job_code || selectedItem.jobCode}
              </li>
              <li>
                <strong>Function:</strong> {getFunctionName(selectedItem)}
              </li>
              <li>
                <strong>Company:</strong> {getCompanyName(selectedItem)}
              </li>
            </ul>
          </div>
        );
      default:
        return <p>Are you sure you want to delete this item?</p>;
    }
  };

  // Assign task to process handlers
  const openAssignModal = async (task) => {
    setSelectedTask(task);
    setSelectedProcessId("");
    setProcessSearchQuery("");
    setAssignModalOpen(true);

    // Load processes for the task's company
    try {
      setLoadingProcesses(true);
      const taskCompanyId = task?.company?.company_id || task?.company_id;

      if (!taskCompanyId) {
        toast.error("Task has no company associated");
        setAvailableProcesses([]);
        return;
      }

      // Fetch processes by company ID using the new API
      const companyProcesses = await getProcessesByCompanyId(taskCompanyId);
      setAvailableProcesses(
        Array.isArray(companyProcesses) ? companyProcesses : [],
      );
    } catch (e) {
      toast.error(e?.message || "Failed to load processes");
      setAvailableProcesses([]);
    } finally {
      setLoadingProcesses(false);
    }
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setSelectedTask(null);
    setSelectedProcessId("");
    setProcessSearchQuery("");
    setAvailableProcesses([]);
  };

  const handleAssignToProcess = () => {
    if (!selectedProcessId || !selectedTask) {
      toast.error("Please select a process");
      return;
    }

    try {
      // Store both the process ID and task ID, then navigate to process edit
      localStorage.setItem("activeProcessId", String(selectedProcessId));
      localStorage.setItem(
        "taskToAdd",
        String(selectedTask.task_id || selectedTask.taskId),
      );
      localStorage.setItem("returnTo", "unassigned-entities");
      setActiveSection("process-edit", { processId: selectedProcessId });
      closeAssignModal();
    } catch (e) {
      toast.error("Failed to navigate to process edit");
    }
  };

  // Filter processes based on search query
  const filteredProcesses = useMemo(() => {
    if (!processSearchQuery.trim()) return availableProcesses;
    const query = processSearchQuery.toLowerCase();
    return availableProcesses.filter((p) => {
      const name = (p.process_name || p.name || "").toLowerCase();
      const code = (p.process_code || p.code || "").toLowerCase();
      return name.includes(query) || code.includes(query);
    });
  }, [availableProcesses, processSearchQuery]);

  // Assign task to job handlers
  const openAssignTaskToJobModal = async (job) => {
    setSelectedJob(job);
    setSelectedTaskId("");
    setTaskSearchQuery("");
    setAssignTaskToJobModalOpen(true);

    // Load tasks for the job's company
    try {
      setLoadingTasks(true);
      const jobCompanyId = job?.company?.company_id || job?.company_id;

      if (!jobCompanyId) {
        toast.error("Job has no company associated");
        setAvailableTasks([]);
        return;
      }

      // Fetch tasks by company ID using the API
      const companyTasks = await getTasksByCompanyId(jobCompanyId);
      setAvailableTasks(Array.isArray(companyTasks) ? companyTasks : []);
    } catch (e) {
      toast.error(e?.message || "Failed to load tasks");
      setAvailableTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const closeAssignTaskToJobModal = () => {
    setAssignTaskToJobModalOpen(false);
    setSelectedJob(null);
    setSelectedTaskId("");
    setTaskSearchQuery("");
    setAvailableTasks([]);
  };

  const handleAssignTaskToJob = () => {
    if (!selectedTaskId || !selectedJob) {
      toast.error("Please select a task");
      return;
    }

    try {
      // Store task ID and job ID, then navigate to task edit
      localStorage.setItem("activeTaskId", String(selectedTaskId));
      localStorage.setItem(
        "jobToAddToTask",
        String(selectedJob.job_id || selectedJob.jobId),
      );
      localStorage.setItem("returnTo", "unassigned-entities");
      localStorage.setItem("returnToTab", "jobsNoTasks"); // Track which tab to return to
      setActiveSection("task-edit", { taskId: selectedTaskId });
      closeAssignTaskToJobModal();
    } catch (e) {
      toast.error("Failed to navigate to task edit");
    }
  };

  // Filter tasks based on search query
  const filteredTasks = useMemo(() => {
    if (!taskSearchQuery.trim()) return availableTasks;
    const query = taskSearchQuery.toLowerCase();
    return availableTasks.filter((t) => {
      const name = (t.task_name || t.name || "").toLowerCase();
      const code = (t.task_code || t.code || "").toLowerCase();
      return name.includes(query) || code.includes(query);
    });
  }, [availableTasks, taskSearchQuery]);

  // Assign floor to job (Jobs without Table) handlers
  const openAssignFloorToJobModal = async (job) => {
    setSelectedJobForFloor(job);
    setModalStep(1); // Start with building selection
    setSelectedBuildingId("");
    setSelectedFloorId("");
    setBuildingSearchQuery("");
    setFloorSearchQuery("");
    setAssignFloorToJobModalOpen(true);

    // Load buildings for the job's company
    try {
      setLoadingBuildings(true);
      const jobCompanyId = job?.company?.company_id || job?.company_id;

      if (!jobCompanyId) {
        toast.error("Job has no company associated");
        setAvailableBuildings([]);
        return;
      }

      // Fetch buildings by company ID
      const companyBuildings = await getBuildingsByCompanyId(jobCompanyId);
      setAvailableBuildings(
        Array.isArray(companyBuildings) ? companyBuildings : [],
      );
    } catch (e) {
      toast.error(e?.message || "Failed to load buildings");
      setAvailableBuildings([]);
    } finally {
      setLoadingBuildings(false);
    }
  };

  const closeAssignFloorToJobModal = () => {
    setAssignFloorToJobModalOpen(false);
    setSelectedJobForFloor(null);
    setModalStep(1);
    setSelectedBuildingId("");
    setSelectedFloorId("");
    setBuildingSearchQuery("");
    setFloorSearchQuery("");
    setAvailableBuildings([]);
    setAvailableFloors([]);
  };

  const handleBuildingSelect = async (buildingId) => {
    setSelectedBuildingId(buildingId);
  };

  const handleNextToFloorSelection = async () => {
    if (!selectedBuildingId) {
      toast.error("Please select a building");
      return;
    }

    try {
      setLoadingFloors(true);
      setModalStep(2); // Move to floor selection

      // Fetch floors for the selected building
      const buildingFloors = await getFloorsByBuildingId(selectedBuildingId);
      setAvailableFloors(Array.isArray(buildingFloors) ? buildingFloors : []);
    } catch (e) {
      toast.error(e?.message || "Failed to load floors");
      setAvailableFloors([]);
    } finally {
      setLoadingFloors(false);
    }
  };

  const handleBackToBuildingSelection = () => {
    setModalStep(1);
    setSelectedFloorId("");
    setFloorSearchQuery("");
    setAvailableFloors([]);
  };

  const handleAssignFloorToJob = () => {
    if (!selectedFloorId || !selectedJobForFloor) {
      toast.error("Please select a floor");
      return;
    }

    try {
      // Set floor for edit mode
      setFloorId(selectedFloorId);
      setFloorFormMode("edit");
      localStorage.setItem("floorEditId", String(selectedFloorId));
      localStorage.setItem("returnTo", "unassigned-entities");
      localStorage.setItem("returnToTab", "jobsNoTable");
      setActiveSection("floor-edit", { floorId: selectedFloorId });
      closeAssignFloorToJobModal();
    } catch (e) {
      toast.error("Failed to navigate to floor edit");
    }
  };

  // Filter buildings based on search query
  const filteredBuildings = useMemo(() => {
    if (!buildingSearchQuery.trim()) return availableBuildings;
    const query = buildingSearchQuery.toLowerCase();
    return availableBuildings.filter((b) => {
      const name = (b.name || "").toLowerCase();
      const code = (b.building_code || b.code || "").toLowerCase();
      return name.includes(query) || code.includes(query);
    });
  }, [availableBuildings, buildingSearchQuery]);

  // Filter floors based on search query
  const filteredFloors = useMemo(() => {
    if (!floorSearchQuery.trim()) return availableFloors;
    const query = floorSearchQuery.toLowerCase();
    return availableFloors.filter((f) => {
      const name = (f.name || "").toLowerCase();
      const code = (f.floor_code || f.code || "").toLowerCase();
      return name.includes(query) || code.includes(query);
    });
  }, [availableFloors, floorSearchQuery]);

  useEffect(() => {
    const totalPages = Math.max(
      1,
      Number(paginationByTab[activeTab]?.totalPages) || 1,
    );
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [activeTab, paginationByTab, currentPage]);

  const renderProcessesTab = () => (
    <div className="role-card">
      {renderSearchBox({
        tabId: "processes",
        value: processesSearchQuery,
        onChange: (e) => {
          setProcessesSearchQuery(e.target.value);
          setCurrentPage(1);
        },
        onClear: () => {
          setProcessesSearchQuery("");
          setCurrentPage(1);
        },
        placeholder: "Search by process name, code, or company...",
        found: paginationByTab.processes.count,
        total: paginationByTab.processes.total,
      })}

      {error && <div className="error-banner">{error}</div>}
      {loading ?
        <div className="no-results">Loading...</div>
      : <div className="roles-table">
          <div
            className="roles-table-header"
            style={{ gridTemplateColumns: "2fr 2fr 1.5fr 140px" }}
          >
            <div className="cell">Company</div>
            <div className="cell">Process Name</div>
            <div className="cell">Process Code</div>
            <div className="cell actions">
              Actions
            </div>
          </div>

          {processesWithoutTasks.length === 0 ?
            <div className="no-results">
              {processesSearchQuery ?
                "No processes match your search"
              : "No processes without tasks found"}
            </div>
          : processesWithoutTasks.map((item, idx) => {
              const itemKey = `process-${item.process_id || item.processId || idx}`;
              const itemId = item.process_id || item.processId;
              return (
                <div
                  key={itemKey}
                  className="roles-table-row"
                  style={{ gridTemplateColumns: "2fr 2fr 1.5fr 140px" }}
                >
                  <div className="cell">{getCompanyName(item)}</div>
                  <div className="cell">
                    {item.process_name || item.processName || "-"}
                  </div>
                  <div className="cell">
                    {item.process_code || item.processCode || "-"}
                  </div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === itemKey}
                        disabled={!itemId}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === itemKey) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(itemKey);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === itemKey && (
                        <div
                          className={`action-dropdown-menu${dropdownDirection === "up" ? " dropdown-up" : ""}`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              try {
                                localStorage.setItem("activeProcessId", String(itemId));
                                localStorage.setItem("returnTo", "unassigned-entities");
                              } catch {}
                              setActiveSection("process-edit", { processId: itemId });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              openDeleteModal(item, "process");
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>
      }
      {renderPagination("processes")}
    </div>
  );

  const renderTasksTab = () => (
    <div className="role-card">
      {renderSearchBox({
        tabId: "tasks",
        value: tasksSearchQuery,
        onChange: (e) => {
          setTasksSearchQuery(e.target.value);
          setCurrentPage(1);
        },
        onClear: () => {
          setTasksSearchQuery("");
          setCurrentPage(1);
        },
        placeholder: "Search by task name, code, or company...",
        found: paginationByTab.tasks.count,
        total: paginationByTab.tasks.total,
      })}

      {error && <div className="error-banner">{error}</div>}
      {loading ?
        <div className="no-results">Loading...</div>
      : <div className="roles-table">
          <div
            className="roles-table-header"
            style={{ gridTemplateColumns: "2fr 2fr 1.5fr 140px" }}
          >
            <div className="cell">Company</div>
            <div className="cell">Task Name</div>
            <div className="cell">Task Code</div>
            <div className="cell actions">Actions</div>
          </div>

          {tasksWithoutProcesses.length === 0 ?
            <div className="no-results">
              {tasksSearchQuery ?
                "No tasks match your search"
              : "No tasks without processes found"}
            </div>
          : tasksWithoutProcesses.map((item, idx) => {
              const itemKey = `task-np-${item.task_id || item.taskId || idx}`;
              const itemId = item.task_id || item.taskId;
              return (
                <div
                  key={itemKey}
                  className="roles-table-row"
                  style={{ gridTemplateColumns: "2fr 2fr 1.5fr 140px" }}
                >
                  <div className="cell">{getCompanyName(item)}</div>
                  <div className="cell">{item.task_name || item.taskName || "-"}</div>
                  <div className="cell">{item.task_code || item.taskCode || "-"}</div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === itemKey}
                        disabled={!itemId}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === itemKey) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(itemKey);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === itemKey && (
                        <div
                          className={`action-dropdown-menu${dropdownDirection === "up" ? " dropdown-up" : ""}`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              try {
                                localStorage.setItem("activeTaskId", String(itemId));
                                localStorage.setItem("returnTo", "unassigned-entities");
                              } catch {}
                              setActiveSection("task-edit", { taskId: itemId });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item assign"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              openAssignModal(item);
                            }}
                          >
                            Assign to Process
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              openDeleteModal(item, "task");
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>
      }
      {renderPagination("tasks")}
    </div>
  );

  const renderTasksWithoutJobsTab = () => (
    <div className="role-card">
      {renderSearchBox({
        tabId: "tasksNoJobs",
        value: tasksNoJobsSearchQuery,
        onChange: (e) => {
          setTasksNoJobsSearchQuery(e.target.value);
          setCurrentPage(1);
        },
        onClear: () => {
          setTasksNoJobsSearchQuery("");
          setCurrentPage(1);
        },
        placeholder: "Search by task name, code, or company...",
        found: paginationByTab.tasksNoJobs.count,
        total: paginationByTab.tasksNoJobs.total,
      })}

      {error && <div className="error-banner">{error}</div>}
      {loading ?
        <div className="no-results">Loading...</div>
      : <div className="roles-table">
          <div
            className="roles-table-header"
            style={{ gridTemplateColumns: "2fr 2fr 1.5fr 140px" }}
          >
            <div className="cell">Company</div>
            <div className="cell">Task Name</div>
            <div className="cell">Task Code</div>
            <div className="cell actions">Actions</div>
          </div>

          {tasksWithoutJobs.length === 0 ?
            <div className="no-results">
              {tasksNoJobsSearchQuery ?
                "No tasks match your search"
              : "No tasks without jobs found"}
            </div>
          : tasksWithoutJobs.map((item, idx) => {
              const itemKey = `task-nj-${item.task_id || item.taskId || idx}`;
              const itemId = item.task_id || item.taskId;
              return (
                <div
                  key={itemKey}
                  className="roles-table-row"
                  style={{ gridTemplateColumns: "2fr 2fr 1.5fr 140px" }}
                >
                  <div className="cell">{getCompanyName(item)}</div>
                  <div className="cell">{item.task_name || item.taskName || "-"}</div>
                  <div className="cell">{item.task_code || item.taskCode || "-"}</div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === itemKey}
                        disabled={!itemId}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === itemKey) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(itemKey);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === itemKey && (
                        <div
                          className={`action-dropdown-menu${dropdownDirection === "up" ? " dropdown-up" : ""}`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              try {
                                localStorage.setItem("activeTaskId", String(itemId));
                                localStorage.setItem("returnTo", "unassigned-entities");
                              } catch {}
                              setActiveSection("task-edit", { taskId: itemId });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              openDeleteModal(item, "task");
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>
      }
      {renderPagination("tasksNoJobs")}
    </div>
  );

  const renderJobsWithoutTasksTab = () => (
    <div className="role-card">
      {renderSearchBox({
        tabId: "jobsNoTasks",
        value: jobsNoTasksSearchQuery,
        onChange: (e) => {
          setJobsNoTasksSearchQuery(e.target.value);
          setCurrentPage(1);
        },
        onClear: () => {
          setJobsNoTasksSearchQuery("");
          setCurrentPage(1);
        },
        placeholder: "Search by job name, code, company, or function...",
        found: paginationByTab.jobsNoTasks.count,
        total: paginationByTab.jobsNoTasks.total,
      })}

      {error && <div className="error-banner">{error}</div>}
      {loading ?
        <div className="no-results">Loading...</div>
      : <div className="roles-table">
          <div
            className="roles-table-header"
            style={{ gridTemplateColumns: "1.5fr 2fr 1.5fr 2fr 140px" }}
          >
            <div className="cell">Company</div>
            <div className="cell">Function Name</div>
            <div className="cell">Job Code</div>
            <div className="cell">Job Name</div>
            <div className="cell actions">Actions</div>
          </div>

          {jobsWithoutTasks.length === 0 ?
            <div className="no-results">
              {jobsNoTasksSearchQuery ?
                "No jobs match your search"
              : "No jobs without tasks found"}
            </div>
          : jobsWithoutTasks.map((item, idx) => {
              const itemKey = `job-nt-${item.job_id || item.jobId || idx}`;
              const itemId = item.job_id || item.jobId;
              return (
                <div
                  key={itemKey}
                  className="roles-table-row"
                  style={{ gridTemplateColumns: "1.5fr 2fr 1.5fr 2fr 140px" }}
                >
                  <div className="cell">{getCompanyName(item)}</div>
                  <div className="cell">{getFunctionName(item)}</div>
                  <div className="cell">{item.job_code || item.jobCode || "-"}</div>
                  <div className="cell">{item.job_name || item.jobName || item.name || "-"}</div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === itemKey}
                        disabled={!itemId}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === itemKey) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(itemKey);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === itemKey && (
                        <div
                          className={`action-dropdown-menu${dropdownDirection === "up" ? " dropdown-up" : ""}`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              try {
                                localStorage.setItem("activeJobId", String(itemId));
                                localStorage.setItem("returnTo", "unassigned-entities");
                              } catch {}
                              setActiveSection("job-edit", { jobId: itemId });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item assign"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              openAssignTaskToJobModal(item);
                            }}
                          >
                            Assign Task
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              openDeleteModal(item, "job");
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>
      }
      {renderPagination("jobsNoTasks")}
    </div>
  );

  const renderJobsWithoutTableTab = () => (
    <div className="role-card">
      {renderSearchBox({
        tabId: "jobsNoTable",
        value: jobsNoTableSearchQuery,
        onChange: (e) => {
          setJobsNoTableSearchQuery(e.target.value);
          setCurrentPage(1);
        },
        onClear: () => {
          setJobsNoTableSearchQuery("");
          setCurrentPage(1);
        },
        placeholder: "Search by job name, code, company, or function...",
        found: paginationByTab.jobsNoTable.count,
        total: paginationByTab.jobsNoTable.total,
      })}

      {error && <div className="error-banner">{error}</div>}
      {loading ?
        <div className="no-results">Loading...</div>
      : <div className="roles-table">
          <div
            className="roles-table-header"
            style={{ gridTemplateColumns: "1.5fr 2fr 1.5fr 2fr 140px" }}
          >
            <div className="cell">Company</div>
            <div className="cell">Function Name</div>
            <div className="cell">Job Code</div>
            <div className="cell">Job Name</div>
            <div className="cell actions">Actions</div>
          </div>

          {jobsWithoutTable.length === 0 ?
            <div className="no-results">
              {jobsNoTableSearchQuery ?
                "No jobs match your search"
              : "No jobs without table found"}
            </div>
          : jobsWithoutTable.map((item, idx) => {
              const itemKey = `job-ntbl-${item.job_id || item.jobId || idx}`;
              const itemId = item.job_id || item.jobId;
              return (
                <div
                  key={itemKey}
                  className="roles-table-row"
                  style={{ gridTemplateColumns: "1.5fr 2fr 1.5fr 2fr 140px" }}
                >
                  <div className="cell">{getCompanyName(item)}</div>
                  <div className="cell">{getFunctionName(item)}</div>
                  <div className="cell">{item.job_code || item.jobCode || "-"}</div>
                  <div className="cell">{item.job_name || item.jobName || item.name || "-"}</div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === itemKey}
                        disabled={!itemId}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === itemKey) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(itemKey);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === itemKey && (
                        <div
                          className={`action-dropdown-menu${dropdownDirection === "up" ? " dropdown-up" : ""}`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              try {
                                localStorage.setItem("activeJobId", String(itemId));
                                localStorage.setItem("returnTo", "unassigned-entities");
                              } catch {}
                              setActiveSection("job-edit", { jobId: itemId });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item assign"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              openAssignFloorToJobModal(item);
                            }}
                          >
                            Assign Floor
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              openDeleteModal(item, "job");
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>
      }
      {renderPagination("jobsNoTable")}
    </div>
  );

  return (
    <div className="page-container unassigned-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Unassigned Entities</h2>
            <p className="page-subtitle">
              View entities that are not properly linked or assigned
            </p>
          </div>
        </div>
      </div>

      <div className="page-content">
        <TopTabs
          tabs={[
            { id: "processes", label: "Processes without tasks" },
            { id: "tasks", label: "Tasks without Process" },
            { id: "tasksNoJobs", label: "Tasks without Jobs" },
            { id: "jobsNoTasks", label: "Jobs without task" },
            { id: "jobsNoTable", label: "Jobs without table" },
          ]}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />

        {activeTab === "processes" && renderProcessesTab()}
        {activeTab === "tasks" && renderTasksTab()}
        {activeTab === "tasksNoJobs" && renderTasksWithoutJobsTab()}
        {activeTab === "jobsNoTasks" && renderJobsWithoutTasksTab()}
        {activeTab === "jobsNoTable" && renderJobsWithoutTableTab()}

        <ConfirmModal
          open={confirmOpen}
          title={`Delete ${
            deleteType === "process" ? "Process"
            : deleteType === "task" ? "Task"
            : "Job"
          }`}
          message={getDeleteMessage()}
          confirmLabel={deleting ? "Deleting..." : "Delete"}
          cancelLabel="Cancel"
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
          busy={deleting}
        />

        {/* Assign Task to Process Modal */}
        {assignModalOpen && (
          <div className="modal-overlay" onClick={closeAssignModal}>
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "650px", width: "90%" }}
            >
              <div className="modal-header">
                <h3>Assign Task to Process</h3>
                <button className="close-btn" onClick={closeAssignModal}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                {selectedTask && (
                  <div
                    style={{
                      padding: "1rem",
                      background:
                        "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
                      borderRadius: "8px",
                      border: "1px solid #667eea30",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <div style={{ marginBottom: "0.5rem" }}>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "#6b7280",
                          textTransform: "uppercase",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Task to Assign
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        color: "#1f2937",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {selectedTask.task_name || selectedTask.taskName}
                    </div>
                    <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                      <strong>Company:</strong> {getCompanyName(selectedTask)}
                    </div>
                  </div>
                )}

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label
                    style={{
                      fontWeight: "600",
                      marginBottom: "0.5rem",
                      display: "block",
                      color: "#374151",
                    }}
                  >
                    Search Processes
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="🔍 Search by process name or code..."
                    value={processSearchQuery}
                    onChange={(e) => setProcessSearchQuery(e.target.value)}
                    style={{
                      padding: "0.75rem 1rem",
                      fontSize: "0.95rem",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      width: "100%",
                      transition: "border-color 0.2s",
                    }}
                  />
                </div>

                <div className="form-group">
                  <label
                    style={{
                      fontWeight: "600",
                      marginBottom: "0.75rem",
                      display: "block",
                      color: "#374151",
                    }}
                  >
                    Select Process{" "}
                    {filteredProcesses.length > 0 && (
                      <span style={{ color: "#6b7280", fontWeight: "normal" }}>
                        ({filteredProcesses.length} available)
                      </span>
                    )}
                  </label>
                  {loadingProcesses ?
                    <div
                      style={{
                        padding: "2rem",
                        textAlign: "center",
                        color: "#6b7280",
                      }}
                    >
                      <div style={{ marginBottom: "0.5rem" }}>⏳</div>
                      Loading processes...
                    </div>
                  : filteredProcesses.length === 0 ?
                    <div
                      style={{
                        padding: "2rem",
                        textAlign: "center",
                        color: "#6b7280",
                        background: "#f9fafb",
                        borderRadius: "8px",
                        border: "2px dashed #d1d5db",
                      }}
                    >
                      {availableProcesses.length === 0 ?
                        "📋 No processes found for this company"
                      : "🔍 No processes match your search"}
                    </div>
                  : <div
                      style={{
                        maxHeight: "320px",
                        overflowY: "auto",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        background: "#fff",
                      }}
                    >
                      {filteredProcesses.map((process, index) => (
                        <label
                          key={process.process_id || process.id}
                          htmlFor={`process-${
                            process.process_id || process.id
                          }`}
                          style={{
                            display: "block",
                            padding: "1rem",
                            cursor: "pointer",
                            borderBottom:
                              index < filteredProcesses.length - 1 ?
                                "1px solid #f3f4f6"
                              : "none",
                            background:
                              (
                                selectedProcessId ===
                                (process.process_id || process.id)
                              ) ?
                                "#eff6ff"
                              : "#fff",
                            transition: "all 0.2s ease",
                            position: "relative",
                          }}
                          onMouseEnter={(e) => {
                            if (
                              selectedProcessId !==
                              (process.process_id || process.id)
                            ) {
                              e.currentTarget.style.background = "#f9fafb";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (
                              selectedProcessId !==
                              (process.process_id || process.id)
                            ) {
                              e.currentTarget.style.background = "#fff";
                            }
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "0.75rem",
                            }}
                          >
                            <input
                              type="radio"
                              id={`process-${process.process_id || process.id}`}
                              name="process-select"
                              checked={
                                selectedProcessId ===
                                (process.process_id || process.id)
                              }
                              onChange={() =>
                                setSelectedProcessId(
                                  process.process_id || process.id,
                                )
                              }
                              style={{
                                marginTop: "0.25rem",
                                cursor: "pointer",
                                width: "18px",
                                height: "18px",
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: "600",
                                  fontSize: "1rem",
                                  color: "#1f2937",
                                  marginBottom: "0.25rem",
                                }}
                              >
                                {process.process_name || process.name}
                              </div>
                              <div
                                style={{
                                  fontSize: "0.875rem",
                                  color: "#6b7280",
                                }}
                              >
                                <span style={{ fontWeight: "500" }}>Code:</span>{" "}
                                {process.process_code || process.code || "N/A"}
                              </div>
                            </div>
                            {selectedProcessId ===
                              (process.process_id || process.id) && (
                              <div
                                style={{
                                  color: "#3b82f6",
                                  fontSize: "1.25rem",
                                  lineHeight: 1,
                                }}
                              >
                                ✓
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  }
                </div>
              </div>
              <div
                className="modal-footer"
                style={{ gap: "0.75rem", paddingTop: "1.25rem" }}
              >
                <button
                  className="secondary-btn"
                  onClick={closeAssignModal}
                  style={{ padding: "0.75rem 1.5rem" }}
                >
                  Cancel
                </button>
                <button
                  className="primary-btn"
                  onClick={handleAssignToProcess}
                  disabled={!selectedProcessId || loadingProcesses}
                  style={{
                    padding: "0.75rem 1.5rem",
                    opacity: !selectedProcessId || loadingProcesses ? 0.5 : 1,
                    cursor:
                      !selectedProcessId || loadingProcesses ? "not-allowed" : (
                        "pointer"
                      ),
                  }}
                >
                  {selectedProcessId ?
                    "✓ Assign to Process"
                  : "Select a Process"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Task to Job Modal */}
        {assignTaskToJobModalOpen && (
          <div className="modal-overlay" onClick={closeAssignTaskToJobModal}>
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "600px", width: "90%" }}
            >
              <div className="modal-header">
                <h3>Assign Task to Job</h3>
                <button
                  className="close-btn"
                  onClick={closeAssignTaskToJobModal}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {selectedJob && (
                  <div
                    style={{
                      padding: "1rem",
                      background:
                        "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
                      borderRadius: "8px",
                      border: "1px solid #667eea30",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <div style={{ marginBottom: "0.5rem" }}>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "#6b7280",
                          textTransform: "uppercase",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Job to Assign
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        color: "#1f2937",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {selectedJob.job_name ||
                        selectedJob.jobName ||
                        selectedJob.name}
                    </div>
                    <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                      <strong>Company:</strong> {getCompanyName(selectedJob)}
                      {getFunctionName(selectedJob) &&
                        getFunctionName(selectedJob) !== "-" && (
                          <>
                            {" "}
                            • <strong>Function:</strong>{" "}
                            {getFunctionName(selectedJob)}
                          </>
                        )}
                    </div>
                  </div>
                )}

                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label
                    style={{
                      fontWeight: "600",
                      marginBottom: "0.5rem",
                      display: "block",
                      color: "#374151",
                    }}
                  >
                    Search Tasks
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="🔍 Search by task name or code..."
                    value={taskSearchQuery}
                    onChange={(e) => setTaskSearchQuery(e.target.value)}
                    style={{
                      padding: "0.75rem 1rem",
                      fontSize: "0.95rem",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      width: "100%",
                      transition: "border-color 0.2s",
                    }}
                  />
                </div>

                <div className="form-group">
                  <label
                    style={{
                      fontWeight: "600",
                      marginBottom: "0.75rem",
                      display: "block",
                      color: "#374151",
                    }}
                  >
                    Select Task{" "}
                    {filteredTasks.length > 0 && (
                      <span style={{ color: "#6b7280", fontWeight: "normal" }}>
                        ({filteredTasks.length} available)
                      </span>
                    )}
                  </label>
                  {loadingTasks ?
                    <div
                      style={{
                        padding: "2rem",
                        textAlign: "center",
                        color: "#6b7280",
                      }}
                    >
                      <div style={{ marginBottom: "0.5rem" }}>⏳</div>
                      Loading tasks...
                    </div>
                  : filteredTasks.length === 0 ?
                    <div
                      style={{
                        padding: "2rem",
                        textAlign: "center",
                        color: "#6b7280",
                        background: "#f9fafb",
                        borderRadius: "8px",
                        border: "2px dashed #d1d5db",
                      }}
                    >
                      {availableTasks.length === 0 ?
                        "📋 No tasks found for this company"
                      : "🔍 No tasks match your search"}
                    </div>
                  : <div
                      style={{
                        maxHeight: "320px",
                        overflowY: "auto",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        background: "#fff",
                      }}
                    >
                      {filteredTasks.map((task, index) => (
                        <label
                          key={task.task_id || task.id}
                          htmlFor={`task-${task.task_id || task.id}`}
                          style={{
                            display: "block",
                            padding: "1rem",
                            cursor: "pointer",
                            borderBottom:
                              index < filteredTasks.length - 1 ?
                                "1px solid #f3f4f6"
                              : "none",
                            background:
                              selectedTaskId === (task.task_id || task.id) ?
                                "#eff6ff"
                              : "#fff",
                            transition: "all 0.2s ease",
                            position: "relative",
                          }}
                          onMouseEnter={(e) => {
                            if (selectedTaskId !== (task.task_id || task.id)) {
                              e.currentTarget.style.background = "#f9fafb";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedTaskId !== (task.task_id || task.id)) {
                              e.currentTarget.style.background = "#fff";
                            }
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "0.75rem",
                            }}
                          >
                            <input
                              type="radio"
                              id={`task-${task.task_id || task.id}`}
                              name="task-select"
                              checked={
                                selectedTaskId === (task.task_id || task.id)
                              }
                              onChange={() =>
                                setSelectedTaskId(task.task_id || task.id)
                              }
                              style={{
                                marginTop: "0.25rem",
                                cursor: "pointer",
                                width: "18px",
                                height: "18px",
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: "600",
                                  fontSize: "1rem",
                                  color: "#1f2937",
                                  marginBottom: "0.25rem",
                                }}
                              >
                                {task.task_name || task.name}
                              </div>
                              <div
                                style={{
                                  fontSize: "0.875rem",
                                  color: "#6b7280",
                                }}
                              >
                                <span style={{ fontWeight: "500" }}>Code:</span>{" "}
                                {task.task_code || task.code || "N/A"}
                              </div>
                            </div>
                            {selectedTaskId === (task.task_id || task.id) && (
                              <div
                                style={{
                                  color: "#3b82f6",
                                  fontSize: "1.25rem",
                                  lineHeight: 1,
                                }}
                              >
                                ✓
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  }
                </div>
              </div>
              <div
                className="modal-footer"
                style={{ gap: "0.75rem", paddingTop: "1.25rem" }}
              >
                <button
                  className="secondary-btn"
                  onClick={closeAssignTaskToJobModal}
                  style={{ padding: "0.75rem 1.5rem" }}
                >
                  Cancel
                </button>
                <button
                  className="primary-btn"
                  onClick={handleAssignTaskToJob}
                  disabled={!selectedTaskId || loadingTasks}
                  style={{
                    padding: "0.75rem 1.5rem",
                    opacity: !selectedTaskId || loadingTasks ? 0.5 : 1,
                    cursor:
                      !selectedTaskId || loadingTasks ? "not-allowed" : (
                        "pointer"
                      ),
                  }}
                >
                  {selectedTaskId ? "✓ Assign to Task" : "Select a Task"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Floor to Job (Jobs without Table) Modal - Two Step */}
        {assignFloorToJobModalOpen && (
          <div className="modal-overlay" onClick={closeAssignFloorToJobModal}>
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "650px", width: "90%" }}
            >
              <div className="modal-header">
                <h3>{modalStep === 1 ? "Select Building" : "Select Floor"}</h3>
                <button
                  className="close-btn"
                  onClick={closeAssignFloorToJobModal}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {selectedJobForFloor && (
                  <div
                    style={{
                      padding: "1rem",
                      background:
                        "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
                      borderRadius: "8px",
                      border: "1px solid #667eea30",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <div style={{ marginBottom: "0.5rem" }}>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "#6b7280",
                          textTransform: "uppercase",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Job to Assign
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        color: "#1f2937",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {selectedJobForFloor.job_name ||
                        selectedJobForFloor.jobName ||
                        selectedJobForFloor.name}
                    </div>
                    <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                      <strong>Company:</strong>{" "}
                      {getCompanyName(selectedJobForFloor)}
                      {getFunctionName(selectedJobForFloor) &&
                        getFunctionName(selectedJobForFloor) !== "-" && (
                          <>
                            {" "}
                            • <strong>Function:</strong>{" "}
                            {getFunctionName(selectedJobForFloor)}
                          </>
                        )}
                    </div>
                  </div>
                )}

                {
                  modalStep === 1 ?
                    // Step 1: Select Building
                    <>
                      <div
                        className="form-group"
                        style={{ marginBottom: "1rem" }}
                      >
                        <label
                          style={{
                            fontWeight: "600",
                            marginBottom: "0.5rem",
                            display: "block",
                            color: "#374151",
                          }}
                        >
                          Search Buildings
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="🔍 Search by building name or code..."
                          value={buildingSearchQuery}
                          onChange={(e) =>
                            setBuildingSearchQuery(e.target.value)
                          }
                          style={{
                            padding: "0.75rem 1rem",
                            fontSize: "0.95rem",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            width: "100%",
                            transition: "border-color 0.2s",
                          }}
                        />
                      </div>

                      <div className="form-group">
                        <label
                          style={{
                            fontWeight: "600",
                            marginBottom: "0.75rem",
                            display: "block",
                            color: "#374151",
                          }}
                        >
                          Select Building{" "}
                          {filteredBuildings.length > 0 && (
                            <span
                              style={{ color: "#6b7280", fontWeight: "normal" }}
                            >
                              ({filteredBuildings.length} available)
                            </span>
                          )}
                        </label>
                        {loadingBuildings ?
                          <div
                            style={{
                              padding: "2rem",
                              textAlign: "center",
                              color: "#6b7280",
                            }}
                          >
                            <div style={{ marginBottom: "0.5rem" }}>⏳</div>
                            Loading buildings...
                          </div>
                        : filteredBuildings.length === 0 ?
                          <div
                            style={{
                              padding: "2rem",
                              textAlign: "center",
                              color: "#6b7280",
                              background: "#f9fafb",
                              borderRadius: "8px",
                              border: "2px dashed #d1d5db",
                            }}
                          >
                            {availableBuildings.length === 0 ?
                              "🏢 No buildings found for this company"
                            : "🔍 No buildings match your search"}
                          </div>
                        : <div
                            style={{
                              maxHeight: "320px",
                              overflowY: "auto",
                              border: "2px solid #e5e7eb",
                              borderRadius: "8px",
                              background: "#fff",
                            }}
                          >
                            {filteredBuildings.map((building, index) => (
                              <label
                                key={building.building_id || building.id}
                                htmlFor={`building-${
                                  building.building_id || building.id
                                }`}
                                style={{
                                  display: "block",
                                  padding: "1rem",
                                  cursor: "pointer",
                                  borderBottom:
                                    index < filteredBuildings.length - 1 ?
                                      "1px solid #f3f4f6"
                                    : "none",
                                  background:
                                    (
                                      selectedBuildingId ===
                                      (building.building_id || building.id)
                                    ) ?
                                      "#eff6ff"
                                    : "#fff",
                                  transition: "all 0.2s ease",
                                  position: "relative",
                                }}
                                onMouseEnter={(e) => {
                                  if (
                                    selectedBuildingId !==
                                    (building.building_id || building.id)
                                  ) {
                                    e.currentTarget.style.background =
                                      "#f9fafb";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (
                                    selectedBuildingId !==
                                    (building.building_id || building.id)
                                  ) {
                                    e.currentTarget.style.background = "#fff";
                                  }
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "0.75rem",
                                  }}
                                >
                                  <input
                                    type="radio"
                                    id={`building-${
                                      building.building_id || building.id
                                    }`}
                                    name="building-select"
                                    checked={
                                      selectedBuildingId ===
                                      (building.building_id || building.id)
                                    }
                                    onChange={() =>
                                      handleBuildingSelect(
                                        building.building_id || building.id,
                                      )
                                    }
                                    style={{
                                      marginTop: "0.25rem",
                                      cursor: "pointer",
                                      width: "18px",
                                      height: "18px",
                                    }}
                                  />
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "1rem",
                                        color: "#1f2937",
                                        marginBottom: "0.25rem",
                                      }}
                                    >
                                      {building.name}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "0.875rem",
                                        color: "#6b7280",
                                      }}
                                    >
                                      <span style={{ fontWeight: "500" }}>
                                        Code:
                                      </span>{" "}
                                      {building.building_code ||
                                        building.code ||
                                        "N/A"}
                                      {building.city && (
                                        <>
                                          {" "}
                                          •{" "}
                                          <span style={{ fontWeight: "500" }}>
                                            City:
                                          </span>{" "}
                                          {building.city}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  {selectedBuildingId ===
                                    (building.building_id || building.id) && (
                                    <div
                                      style={{
                                        color: "#3b82f6",
                                        fontSize: "1.25rem",
                                        lineHeight: 1,
                                      }}
                                    >
                                      ✓
                                    </div>
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>
                        }
                      </div>
                    </>
                    // Step 2: Select Floor
                  : <>
                      <div
                        className="form-group"
                        style={{ marginBottom: "1rem" }}
                      >
                        <label
                          style={{
                            fontWeight: "600",
                            marginBottom: "0.5rem",
                            display: "block",
                            color: "#374151",
                          }}
                        >
                          Search Floors
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="🔍 Search by floor name or code..."
                          value={floorSearchQuery}
                          onChange={(e) => setFloorSearchQuery(e.target.value)}
                          style={{
                            padding: "0.75rem 1rem",
                            fontSize: "0.95rem",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            width: "100%",
                            transition: "border-color 0.2s",
                          }}
                        />
                      </div>

                      <div className="form-group">
                        <label
                          style={{
                            fontWeight: "600",
                            marginBottom: "0.75rem",
                            display: "block",
                            color: "#374151",
                          }}
                        >
                          Select Floor{" "}
                          {filteredFloors.length > 0 && (
                            <span
                              style={{ color: "#6b7280", fontWeight: "normal" }}
                            >
                              ({filteredFloors.length} available)
                            </span>
                          )}
                        </label>
                        {loadingFloors ?
                          <div
                            style={{
                              padding: "2rem",
                              textAlign: "center",
                              color: "#6b7280",
                            }}
                          >
                            <div style={{ marginBottom: "0.5rem" }}>⏳</div>
                            Loading floors...
                          </div>
                        : filteredFloors.length === 0 ?
                          <div
                            style={{
                              padding: "2rem",
                              textAlign: "center",
                              color: "#6b7280",
                              background: "#f9fafb",
                              borderRadius: "8px",
                              border: "2px dashed #d1d5db",
                            }}
                          >
                            {availableFloors.length === 0 ?
                              "📋 No floors found for this building"
                            : "🔍 No floors match your search"}
                          </div>
                        : <div
                            style={{
                              maxHeight: "320px",
                              overflowY: "auto",
                              border: "2px solid #e5e7eb",
                              borderRadius: "8px",
                              background: "#fff",
                            }}
                          >
                            {filteredFloors.map((floor, index) => (
                              <label
                                key={floor.floor_id || floor.id}
                                htmlFor={`floor-${floor.floor_id || floor.id}`}
                                style={{
                                  display: "block",
                                  padding: "1rem",
                                  cursor: "pointer",
                                  borderBottom:
                                    index < filteredFloors.length - 1 ?
                                      "1px solid #f3f4f6"
                                    : "none",
                                  background:
                                    (
                                      selectedFloorId ===
                                      (floor.floor_id || floor.id)
                                    ) ?
                                      "#eff6ff"
                                    : "#fff",
                                  transition: "all 0.2s ease",
                                  position: "relative",
                                }}
                                onMouseEnter={(e) => {
                                  if (
                                    selectedFloorId !==
                                    (floor.floor_id || floor.id)
                                  ) {
                                    e.currentTarget.style.background =
                                      "#f9fafb";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (
                                    selectedFloorId !==
                                    (floor.floor_id || floor.id)
                                  ) {
                                    e.currentTarget.style.background = "#fff";
                                  }
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "0.75rem",
                                  }}
                                >
                                  <input
                                    type="radio"
                                    id={`floor-${floor.floor_id || floor.id}`}
                                    name="floor-select"
                                    checked={
                                      selectedFloorId ===
                                      (floor.floor_id || floor.id)
                                    }
                                    onChange={() =>
                                      setSelectedFloorId(
                                        floor.floor_id || floor.id,
                                      )
                                    }
                                    style={{
                                      marginTop: "0.25rem",
                                      cursor: "pointer",
                                      width: "18px",
                                      height: "18px",
                                    }}
                                  />
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "1rem",
                                        color: "#1f2937",
                                        marginBottom: "0.25rem",
                                      }}
                                    >
                                      {floor.name}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "0.875rem",
                                        color: "#6b7280",
                                      }}
                                    >
                                      <span style={{ fontWeight: "500" }}>
                                        Code:
                                      </span>{" "}
                                      {floor.floor_code || floor.code || "N/A"}
                                    </div>
                                  </div>
                                  {selectedFloorId ===
                                    (floor.floor_id || floor.id) && (
                                    <div
                                      style={{
                                        color: "#3b82f6",
                                        fontSize: "1.25rem",
                                        lineHeight: 1,
                                      }}
                                    >
                                      ✓
                                    </div>
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>
                        }
                      </div>
                    </>

                }
              </div>
              <div
                className="modal-footer"
                style={{ gap: "0.75rem", paddingTop: "1.25rem" }}
              >
                {modalStep === 2 && (
                  <button
                    className="secondary-btn"
                    onClick={handleBackToBuildingSelection}
                    style={{ padding: "0.75rem 1.5rem" }}
                  >
                    ← Back
                  </button>
                )}
                <button
                  className="secondary-btn"
                  onClick={closeAssignFloorToJobModal}
                  style={{ padding: "0.75rem 1.5rem" }}
                >
                  Cancel
                </button>
                {modalStep === 1 ?
                  <button
                    className="primary-btn"
                    onClick={handleNextToFloorSelection}
                    disabled={!selectedBuildingId || loadingBuildings}
                    style={{
                      padding: "0.75rem 1.5rem",
                      opacity:
                        !selectedBuildingId || loadingBuildings ? 0.5 : 1,
                      cursor:
                        !selectedBuildingId || loadingBuildings ? "not-allowed"
                        : "pointer",
                    }}
                  >
                    {selectedBuildingId ?
                      "Next: Select Floor →"
                    : "Select a Building"}
                  </button>
                : <button
                    className="primary-btn"
                    onClick={handleAssignFloorToJob}
                    disabled={!selectedFloorId || loadingFloors}
                    style={{
                      padding: "0.75rem 1.5rem",
                      opacity: !selectedFloorId || loadingFloors ? 0.5 : 1,
                      cursor:
                        !selectedFloorId || loadingFloors ? "not-allowed" : (
                          "pointer"
                        ),
                    }}
                  >
                    {selectedFloorId ? "✓ Open Floor Layout" : "Select a Floor"}
                  </button>
                }
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnassignedEntitiesPage;

